import AWS from "aws-sdk";
import appConfig from "./appConfig";
import {
  HumanizeDuration,
  HumanizeDurationLanguage,
} from "humanize-duration-ts";

export const isValidS3Uri = (s3Uri: string) => {
  return (
    s3Uri.startsWith("s3://") &&
    s3Uri.length > 5 &&
    s3Uri.split("/").length > 3 &&
    !s3Uri.endsWith("/")
  );
};

export const splitS3Uri = (s3Uri: string): [string, string] => {
  const parts = s3Uri.replace(/^s3:\/\//, "").split("/");
  const bucket = parts[0];
  const key = parts.slice(1).join("/");
  return [bucket, key];
};

export const getSignedS3Url = (bucket: string, key: string) => {
  const s3 = new AWS.S3({
    endpoint: `s3.${appConfig.awsRegion}.${appConfig.awsUrlSuffix}`,
  });

  const signedUrl = s3.getSignedUrl("getObject", {
    Bucket: bucket,
    Key: key,
    Expires: 600,
  });
  return signedUrl;
};

export const getSignedS3UrlFromS3Uri = (s3Url: string) => {
  const [bucket, key] = splitS3Uri(s3Url);
  return getSignedS3Url(bucket, key);
};

export const s3UrlToHttpsUrl = (s3Url: string) => {
  const bucketAndPrefix = s3Url.replace("s3://", "");
  const parts = bucketAndPrefix.split("/");
  const bucket = parts.shift() || "";
  const prefix = parts.join("/");
  const httpsUrl = `https://${bucket}.s3.amazonaws.com/${prefix}`;
  return httpsUrl;
};

export const getRandomHexString = (len: number = 6) => {
  let str = "";
  for (let i = 0; i < len; i++) {
    str += (crypto.getRandomValues(new Uint8Array(1))[0] % 15).toString(16);
  }
  return str;
};

export const getDateTimeString = (milliseconds: string) => {
  try {
    return new Date(milliseconds).toLocaleDateString("en", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
  } catch {
    return "Not available";
  }
};

export const getDurationBetweenDateTimes = (
  startMilliseconds: string,
  endMilliseconds: string
) => {
  try {
    const startDate = new Date(startMilliseconds);
    const endDate = new Date(endMilliseconds);
    const diffInSeconds = (endDate.getTime() - startDate.getTime()) / 1000;

    let durationString = "";
    const numDays = Math.floor(diffInSeconds / 86400);
    if (numDays >= 1) {
      durationString += `${numDays} day${numDays >= 2 ? "s" : ""}`;
    }
    const numHours = Math.floor((diffInSeconds % 86400) / 3600);
    if (numHours >= 1) {
      durationString += `${durationString === "" ? "" : " "}${numHours} hour${
        numHours >= 2 ? "s" : ""
      }`;
    }
    const numMinutes = Math.floor(((diffInSeconds % 86400) % 3600) / 60);
    if (numMinutes >= 1) {
      durationString += `${
        durationString === "" ? "" : " "
      }${numMinutes} minute${numMinutes >= 2 ? "s" : ""}`;
    }
    const numSeconds = ((diffInSeconds % 86400) % 3600) % 60;
    if (numSeconds >= 1) {
      durationString += `${
        durationString === "" ? "" : " "
      }${numSeconds} second${numSeconds >= 2 ? "s" : ""}`;
    }

    return durationString;
  } catch {
    return "Not available";
  }
};

export function humanizeDuration(ms: number) {
  const humanizer = new HumanizeDuration(new HumanizeDurationLanguage());
  const result = humanizer.humanize(Math.floor(ms / 1000) * 1000);
  // Convert  results like "6 days, 12 hours, 19 minutes, 5 seconds" to just "6 days, 12 hours"
  const matches = result.match(/^([^,]+,[^,]+)/);
  if (matches) {
    return matches[0];
  }
  return result;
}
